import React from 'react';
import { Typography, Table, Row, Col, Divider, Space, Tag } from 'antd';
import { formatCurrency } from '../utils/currency';

const { Title, Text } = Typography;

const A4Invoice = ({ invoice, company, branding, paymentMethod, logoUrl }) => (
    <div id="invoice-print-area" className="a4-invoice" style={{ padding: '40px', background: '#fff', color: '#000', fontFamily: 'Inter, -apple-system, sans-serif' }}>
        {/* Header / Branding */}
        <Row justify="space-between" align="top" style={{ marginBottom: 40 }}>
            <Col span={12}>
                {logoUrl ? (
                    <img src={logoUrl} alt="logo" style={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain', marginBottom: 16 }} />
                ) : (
                    <Title level={3} style={{ margin: '0 0 16px 0', color: '#1890ff' }}>{company?.name || 'MAKER AI POS'}</Title>
                )}
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
                <Title level={4} style={{ margin: '0 0 4px 0' }}>{company?.name}</Title>
                <Text style={{ display: 'block' }}>{company?.address}</Text>
                {company?.town && <Text style={{ display: 'block' }}>{company.town}{company.zip ? `, ${company.zip}` : ''}{company.country_code ? ` - ${company.country_code}` : ''}</Text>}
                {company?.phone && <Text style={{ display: 'block' }}>Phone: {company.phone}</Text>}
                {company?.email && <Text style={{ display: 'block' }}>Email: {company.email}</Text>}
                {company?.tva_intra && <Text style={{ display: 'block', fontWeight: 'bold' }}>VAT: {company.tva_intra}</Text>}
            </Col>
        </Row>

        <Divider style={{ margin: '20px 0', borderColor: '#eee' }} />

        {/* Invoice Meta Data */}
        <Row justify="space-between" align="bottom" style={{ marginBottom: 30 }}>
            <Col>
                <div style={{ background: '#f9f9f9', padding: '12px 20px', borderRadius: '4px', borderLeft: '4px solid #1890ff' }}>
                    <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Bill To</Text>
                    <Title level={5} style={{ margin: '4px 0 0 0' }}>{invoice.socname || 'Walking Customer'}</Title>
                </div>
            </Col>
            <Col style={{ textAlign: 'right' }}>
                <Title level={2} style={{ margin: '0 0 8px 0', color: '#333' }}>INVOICE</Title>
                <Space direction="vertical" align="end" size={0}>
                    <Text strong>REF: {invoice.ref}</Text>
                    <Text>Date: {new Date(invoice.date * 1000).toLocaleDateString()}</Text>
                    {parseFloat(invoice.remaintopay) <= 0.001 ? (
                        <Tag color="success" style={{ marginTop: 8, borderRadius: '12px', padding: '0 12px' }}>PAID</Tag>
                    ) : (
                        <Tag color="warning" style={{ marginTop: 8, borderRadius: '12px', padding: '0 12px' }}>UNPAID</Tag>
                    )}
                </Space>
            </Col>

        </Row>

        {/* Lines Table */}
        <Table
            dataSource={invoice.lines}
            columns={[
                {
                    title: 'Description',
                    dataIndex: 'libelle',
                    key: 'libelle',
                    render: (t, r) => (
                        <div style={{ textAlign: 'left' }}>
                            <Text strong>{t}</Text>
                            {r.description && <div style={{ fontSize: '11px', color: '#666' }}>{r.description}</div>}
                        </div>
                    )
                },
                { title: 'Qty', dataIndex: 'qty', key: 'qty', align: 'right', width: 60 },
                { title: 'Unit Price', dataIndex: 'subprice', key: 'subprice', align: 'right', render: formatCurrency, width: 100 },
                { title: 'VAT', dataIndex: 'tva_tx', key: 'tva_tx', align: 'right', render: t => `${parseFloat(t)}%`, width: 70 },
                { title: 'Total', dataIndex: 'total_ttc', key: 'total_ttc', align: 'right', render: formatCurrency, width: 120 },
            ]}
            pagination={false}
            rowKey="id"
            size="small"
            className="invoice-table"
            summary={() => {
                const adjs = invoice.adjustments || [];
                return (
                    <>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={4} align="right"><Text type="secondary">Subtotal (HT)</Text></Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">{formatCurrency(invoice.total_ht)}</Table.Summary.Cell>
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={4} align="right"><Text type="secondary">Total VAT</Text></Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">{formatCurrency(invoice.total_tva)}</Table.Summary.Cell>
                        </Table.Summary.Row>
                        {adjs.map((adj, i) => (
                            <Table.Summary.Row key={`adj-${i}`}>
                                <Table.Summary.Cell index={0} colSpan={4} align="right">
                                    <Text type="secondary">{adj.label || (adj.kind === 'DISCOUNT' ? 'Discount' : 'Fee')}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    <Text style={{ color: adj.kind === 'DISCOUNT' ? '#cf1322' : '#3f8600' }}>
                                        {adj.kind === 'DISCOUNT' ? '-' : '+'}{formatCurrency(adj.amount)}
                                    </Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        ))}
                        <Table.Summary.Row style={{ background: '#fafafa' }}>
                            <Table.Summary.Cell index={0} colSpan={4} align="right">
                                <Title level={4} style={{ margin: 0, color: '#1890ff' }}>Grand Total</Title>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                                <Title level={4} style={{ margin: 0, color: '#1890ff' }}>{formatCurrency(invoice.total_ttc)}</Title>
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    </>
                );
            }}
        />

        <div style={{ marginTop: 30, padding: '20px', background: '#fffcf5', border: '1px solid #ffe58f', borderRadius: '4px' }}>
            <Row gutter={40}>
                <Col span={12}>
                    <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: 8, color: '#856404' }}>PAYMENT INFO</Text>
                    <Text>Method: <b>{paymentMethod === 6 ? 'CliQ' : 'Cash'}</b></Text><br />
                    <Text>Status: <b>Settled / Paid</b></Text>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                    <Text italic style={{ color: '#8c8c8c' }}>Thank you for choosing {company?.name}!</Text>
                </Col>
            </Row>
        </div>
    </div>
);

const ThermalReceipt = ({ invoice, company, branding, paymentMethod, logoUrl }) => (
    <div id="invoice-print-area" className="thermal-receipt" style={{
        width: '100%',
        padding: '2mm',
        background: '#fff',
        color: '#000',
        fontFamily: "'Inter', -apple-system, sans-serif",
        fontSize: '12px',
        lineHeight: '1.4'
    }}>
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
            {logoUrl && <img src={logoUrl} alt="logo" style={{ maxHeight: '50px', maxWidth: '80%', marginBottom: '8px', filter: 'grayscale(100%)' }} />}
            <div style={{ fontWeight: '800', fontSize: '16px', textTransform: 'uppercase', marginBottom: '2px' }}>{company?.name}</div>
            <div style={{ fontSize: '11px', color: '#333' }}>{company?.address}</div>
            {company?.phone && <div style={{ fontSize: '11px' }}>Tel: {company.phone}</div>}
            {company?.tva_intra && <div style={{ fontSize: '11px', marginTop: '1px' }}>VAT: {company.tva_intra}</div>}
        </div>

        {/* Custom Headers */}
        {(branding?.header_en || branding?.header_ar) && (
            <div style={{ textAlign: 'center', marginBottom: '10px', fontSize: '11px', borderTop: '1px solid #eee', paddingTop: '5px' }}>
                {branding.header_ar && <div style={{ direction: 'rtl', marginBottom: '2px' }}>{branding.header_ar}</div>}
                {branding.header_en && <div>{branding.header_en}</div>}
            </div>
        )}

        <div style={{ borderTop: '1px dashed #000', margin: '8px 0' }}></div>

        {/* Invoice Info */}
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
            <div style={{ fontWeight: '700', fontSize: '14px', letterSpacing: '1px' }}>INVOICE / RECEIPT</div>
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginTop: '2px' }}>{invoice.ref}</div>
            <div style={{ fontSize: '11px', color: '#444' }}>
                {new Date(invoice.date * 1000).toLocaleDateString()} {new Date(invoice.date * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
        </div>

        <div style={{ borderTop: '1px dashed #000', margin: '8px 0' }}></div>

        {/* Items Table */}
        <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #000', paddingBottom: '3px', marginBottom: '5px', fontWeight: 'bold', fontSize: '10px', textTransform: 'uppercase' }}>
                <span style={{ flex: '1' }}>Item</span>
                <span style={{ width: '35px', textAlign: 'center' }}>Qty</span>
                <span style={{ width: '70px', textAlign: 'right' }}>Price</span>
                <span style={{ width: '75px', textAlign: 'right' }}>Total</span>
            </div>
            {invoice.lines.map((item, idx) => (
                <div key={idx} style={{ marginBottom: '6px', borderBottom: '0.5px solid #eee', paddingBottom: '4px' }}>
                    <div style={{ fontWeight: '600', fontSize: '12px', marginBottom: '1px' }}>{item.libelle}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', fontSize: '11px' }}>
                        <span style={{ flex: '1' }}></span>
                        <span style={{ width: '35px', textAlign: 'center' }}>{item.qty}</span>
                        <span style={{ width: '70px', textAlign: 'right' }}>{formatCurrency(item.subprice)}</span>
                        <span style={{ width: '75px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(item.total_ht || (parseFloat(item.qty) * parseFloat(item.subprice)))}</span>
                    </div>
                </div>
            ))}
        </div>

        {/* Totals Section */}
        <div style={{ marginLeft: 'auto', width: '100%', borderTop: '1px solid #000', paddingTop: '5px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '11px' }}>
                <span>Subtotal (Net):</span>
                <span>{formatCurrency(invoice.total_ht)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '11px' }}>
                <span>VAT Total:</span>
                <span>{formatCurrency(invoice.total_tva)}</span>
            </div>
            {(invoice.adjustments || []).map((adj, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '11px' }}>
                    <span>{adj.label}:</span>
                    <span>{adj.kind === 'DISCOUNT' ? '-' : '+'}{formatCurrency(adj.amount)}</span>
                </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '900', fontSize: '18px', marginTop: '8px', borderTop: 'double 3px #000', paddingTop: '5px' }}>
                <span>TOTAL:</span>
                <span>{formatCurrency(invoice.total_ttc)}</span>
            </div>
        </div>

        <div style={{ borderTop: '1px dashed #000', margin: '15px 0 10px 0' }}></div>

        {/* Payment & Footer */}
        <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '10px', fontSize: '12px' }}>
                Payment Method: <span style={{ fontWeight: 'bold' }}>{paymentMethod === 6 ? 'CliQ' : 'Cash'}</span>
            </div>

            {(branding?.footer_ar || branding?.footer_en) ? (
                <div style={{ fontSize: '11px', marginTop: '10px' }}>
                    {branding.footer_ar && <div style={{ direction: 'rtl', marginBottom: '4px', fontWeight: 'bold' }}>{branding.footer_ar}</div>}
                    {branding.footer_en && <div style={{ fontStyle: 'italic' }}>{branding.footer_en}</div>}
                </div>
            ) : (
                <div style={{ fontStyle: 'italic', fontSize: '11px', marginTop: '10px' }}>Thank you for your visit!</div>
            )}

            <div style={{ borderTop: '1px solid #eee', marginTop: '15px', paddingTop: '5px', fontSize: '9px', color: '#999', textTransform: 'uppercase' }}>
                Powered by Maker AI POS
            </div>
        </div>
    </div>
);

const InvoicePrint = ({ invoice, company, branding, paymentMethod }) => {
    if (!invoice) return null;

    const logoUrl = branding?.logo_url ? `/api${branding.logo_url}?t=${Date.now()}` : null;
    const isThermal = /80mm|57mm/.test(branding?.paper_size || '');

    return (
        <div data-paper-size={branding?.paper_size || 'A4'} data-is-thermal={isThermal}>
            {isThermal ? (
                <ThermalReceipt
                    invoice={invoice}
                    company={company}
                    branding={branding}
                    paymentMethod={paymentMethod}
                    logoUrl={logoUrl}
                />
            ) : (
                <A4Invoice
                    invoice={invoice}
                    company={company}
                    branding={branding}
                    paymentMethod={paymentMethod}
                    logoUrl={logoUrl}
                />
            )}

            <style>{`
                @media screen {
                    .thermal-receipt {
                        width: 80mm !important;
                        margin: 0 auto;
                        box-shadow: 0 0 10px rgba(0,0,0,0.1);
                        padding: 10mm !important;
                    }
                }

                @media print {
                    @page { 
                        size: ${isThermal ? (branding?.paper_size === '57mm' ? '57mm auto' : '80mm auto') : 'A4'} !important; 
                        margin: 0 !important; 
                    }
                    body { 
                        margin: 0 !important; 
                        padding: 0 !important; 
                        background: #fff !important; 
                        width: ${isThermal ? (branding?.paper_size === '57mm' ? '57mm' : '80mm') : 'auto'} !important;
                    }
                    #invoice-print-area { 
                        padding: ${isThermal ? '2mm' : '0'} !important; 
                        width: 100% !important; 
                        box-shadow: none !important;
                    }
                    .no-print { display: none !important; }
                    .ant-table-summary { background: transparent !important; }
                }
            `}</style>
        </div>
    );
};

export default InvoicePrint;
